import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { communicationsApi } from '../../api/communications';

/**
 * TemplatePickerModal - Shared modal for selecting templates
 * Used in Broadcast and Announcements
 */
const TemplatePickerModal = ({ isOpen, onClose, onSelectTemplate, preferredLang = 'en' }) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLang, setSelectedLang] = useState(preferredLang);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'knowledge_pack_notifications', label: 'Knowledge Pack Updates' },
    { value: 'emzi_nexus_announcements', label: 'EMZI Nexus Announcements' },
    { value: 'events_promotions', label: 'Events & Promotions' },
    { value: 'internal_notifications', label: 'Internal Notifications' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchTerm, selectedCategory, selectedLang]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await communicationsApi.templates.list({ active: true });
      setTemplates(response.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTemplates = () => {
    let filtered = [...templates];

    // Filter by language
    if (selectedLang !== 'all') {
      filtered = filtered.filter(t => t.lang === selectedLang);
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(term) ||
        t.content.toLowerCase().includes(term)
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleSelectTemplate = (template) => {
    onSelectTemplate(template);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Select Template</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="col-span-1"
            />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="col-span-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <select
              value={selectedLang}
              onChange={(e) => setSelectedLang(e.target.value)}
              className="col-span-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Languages</option>
              <option value="en">English</option>
              <option value="ms">Bahasa Melayu</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Template List */}
          <div className="w-1/2 border-r border-gray-200 overflow-y-auto p-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading templates...</p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No templates found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTemplates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => setPreviewTemplate(template)}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      previewTemplate?.id === template.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold text-sm text-gray-900">{template.title}</h3>
                      <Badge className="ml-2 text-xs">
                        {template.lang.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      {template.category.replace(/_/g, ' ')}
                    </p>
                    <p className="text-xs text-gray-600 line-clamp-2">
                      {template.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview Pane */}
          <div className="w-1/2 overflow-y-auto p-4">
            {previewTemplate ? (
              <div>
                <h3 className="font-bold text-lg mb-2">{previewTemplate.title}</h3>
                <div className="flex gap-2 mb-4">
                  <Badge>{previewTemplate.lang.toUpperCase()}</Badge>
                  <Badge variant="outline">
                    v{previewTemplate.version}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {previewTemplate.category.replace(/_/g, ' ')}
                  </Badge>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Template Content:</h4>
                  <div className="whitespace-pre-wrap text-sm text-gray-900">
                    {previewTemplate.content}
                  </div>
                </div>

                {previewTemplate.tokens && previewTemplate.tokens.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Available Tokens:</h4>
                    <div className="flex flex-wrap gap-2">
                      {previewTemplate.tokens.map(token => (
                        <Badge key={token} variant="outline" className="text-xs">
                          {token}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-xs text-gray-500 mb-4">
                  <p>Updated by: {previewTemplate.updated_by}</p>
                  <p>Last updated: {new Date(previewTemplate.updated_at).toLocaleString()}</p>
                </div>

                <Button
                  onClick={() => handleSelectTemplate(previewTemplate)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Use This Template
                </Button>
              </div>
            ) : (
              <div className="text-center text-gray-500 mt-20">
                <p>Select a template to preview</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePickerModal;
